import React from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  TableBody,
  makeStyles,
  IconButton,
  Collapse,
  Box,
  CircularProgress,
  withStyles,
  Theme,
  createStyles,
  useMediaQuery,
  Hidden,
  SvgIconProps,
  Tooltip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import WarningIcon from "@material-ui/icons/Warning";
import AssignmentLateIcon from "@material-ui/icons/AssignmentLate";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { Batch } from "../../model/batch";

const useStyles = makeStyles({
  root: {
    maxWidth: "100%",
  },
});

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

const TableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      padding: 6,
      [theme.breakpoints.up("sm")]: {
        padding: 8,
      },
      [theme.breakpoints.up("md")]: {
        padding: 16,
      },
    },
    body: {
      padding: 6,
      [theme.breakpoints.up("sm")]: {
        padding: 8,
      },
      [theme.breakpoints.up("md")]: {
        padding: 16,
      },
    },
  }),
)(MuiTableCell);

const SampleRequestedIcon = (props: SvgIconProps) => <AssignmentLateIcon color="secondary" {...props} />;
const ProductImageOutdatedIcon = (props: SvgIconProps) => <WarningIcon color="secondary" {...props} />;
const SampleRequestedIconWithTooltip = (props: SvgIconProps) => (
  <Tooltip title="Näyte lähetettävä" placement="top">
    <SampleRequestedIcon {...props} />
  </Tooltip>
);
const ProductImageOutdatedIconWithTooltip = (props: SvgIconProps) => (
  <Tooltip title="Tuotekuva vanhentunut" placement="top">
    <ProductImageOutdatedIcon {...props} />
  </Tooltip>
);

const formatAbv = (value: number) => Number(value).toLocaleString("fi", { minimumFractionDigits: 1 });
const formatVolume = (value: number) => Number(value).toLocaleString("fi", { minimumFractionDigits: 2 });

const useAlertStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    icon: {
      color: theme.palette.secondary.main,
    },
    standardWarning: {
      background: "#F9E9EA",
      color: theme.palette.text.primary,
    },
  }),
);

function Row({ row, cols }: { row: Batch; cols: number }) {
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();
  const alertClasses = useAlertStyles();
  return (
    <>
      <TableRow className={classes.root}>
        <TableCell component="th" scope="row">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {row.batchNumber}
        </TableCell>
        <TableCell align="left">{row.batchId}</TableCell>
        <TableCell align="left">{row.productNumber}</TableCell>
        <TableCell align="right">{row.quantity}</TableCell>
        <Hidden smDown>
          <TableCell align="right">{row.vintage}</TableCell>
        </Hidden>
        <Hidden smDown>
          <TableCell align="center">{row.sampleRequested && <SampleRequestedIconWithTooltip />}</TableCell>
          <TableCell align="center">{row.productImageOutdated && <ProductImageOutdatedIconWithTooltip />}</TableCell>
        </Hidden>
        <Hidden mdUp>
          <TableCell align="center">
            {row.sampleRequested && <SampleRequestedIconWithTooltip />}
            {row.productImageOutdated && <ProductImageOutdatedIconWithTooltip />}
          </TableCell>
        </Hidden>

        <Hidden mdDown>
          <TableCell align="right">{formatVolume(row.volume)} l</TableCell>
          <TableCell align="right">{formatAbv(row.abv)} %</TableCell>
        </Hidden>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={cols}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box my={1}>
              <strong>Luotu:</strong> {format(parseISO(row.created), "dd.MM.yyyy HH:mm:ss")},{" "}
              <Box whiteSpace="nowrap" display="inline">
                {row.createdBy}
              </Box>
            </Box>
            <Box my={1}>
              <strong>Tilavuus:</strong> {formatVolume(row.volume)} l
            </Box>
            <Box my={1}>
              <strong>Alkoholi:</strong> {formatAbv(row.abv)} %
            </Box>
            {row.additionalInformation && (
              <Box my={1}>
                <strong>Lisätiedot:</strong> {row.additionalInformation}
              </Box>
            )}
            {row.sampleRequested && (
              <Box my={1}>
                <Alert
                  icon={<SampleRequestedIcon fontSize="inherit" />}
                  severity="warning"
                  classes={{
                    root: alertClasses.root,
                    icon: alertClasses.icon,
                    standardWarning: alertClasses.standardWarning,
                  }}
                >
                  Näyte lähetettävä
                </Alert>
              </Box>
            )}
            {row.productImageOutdated && (
              <Box my={1}>
                <Alert
                  icon={<ProductImageOutdatedIcon fontSize="inherit" />}
                  severity="warning"
                  classes={{
                    root: alertClasses.root,
                    icon: alertClasses.icon,
                    standardWarning: alertClasses.standardWarning,
                  }}
                >
                  Tuotekuva vanhentunut
                </Alert>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

interface TableProps {
  data: Batch[];
  loading: boolean;
}
export const BatchTable = ({ data, loading }: TableProps): JSX.Element => {
  const classes = useStyles();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const cols = smDown ? 7 : 10;
  return (
    <Box component="div" overflow="hidden" style={{ width: "100%" }}>
      <Paper className={classes.root}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">#</TableCell>
                <Hidden only="xs">
                  <TableCell align="left">Erätunnus</TableCell>
                </Hidden>
                <Hidden smUp>
                  <TableCell align="left">Erä</TableCell>
                </Hidden>
                <TableCell align="left">
                  <Hidden smDown>Tuotenumero</Hidden>
                  <Hidden mdUp>Tuote</Hidden>
                </TableCell>
                <TableCell align="right">Kpl</TableCell>
                <Hidden smDown>
                  <TableCell align="right">Vuosikerta</TableCell>
                </Hidden>
                <Hidden smDown>
                  <TableCell align="center">Näyte?</TableCell>
                  <TableCell align="center">Tuotekuva?</TableCell>
                </Hidden>
                <Hidden mdUp>
                  <TableCell align="center">Huom.</TableCell>
                </Hidden>

                <Hidden mdDown>
                  <TableCell align="right">Tilavuus</TableCell>
                  <TableCell align="right">Alkoholi</TableCell>
                </Hidden>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={cols} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols} align="center">
                    Ei tuloksia
                  </TableCell>
                </TableRow>
              )}
              {data.map((row: Batch) => (
                <Row key={row.id} row={row} cols={cols} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
